import { useCallback, useEffect, useState } from 'react';
import { FILTER_CLAUSE } from 'src/components/ui/FilterPlate/constants';
import {
	isISODurationFilterValue,
	parseISODurationFilter,
} from 'src/utils/helpers/parseISODurationFilter';

const DATE_TIME_OPS = [
	FILTER_CLAUSE.last,
	FILTER_CLAUSE.more,
	FILTER_CLAUSE.less,
];

export const useFilter = (
	options,
	initialSelected,
	operatorFromUrl,
	onSubmit,
	type,
) => {
	const [selectedOption, setSelectedOption] = useState(options[0]);
	const [selectedValue, setSelectedValue] = useState(initialSelected || '');
	const [error, setError] = useState({
		isError: false,
		message: '',
	});

	// useEffect(() => {
	// 	console.log('useFilter 1: ', selectedOption, selectedValue);
	// }, [selectedOption, selectedValue]);

	useEffect(() => {
		let op = operatorFromUrl;
		let initialValue = initialSelected;

		if (operatorFromUrl === 'isNull' && initialValue === false) {
			op = 'isNotNull';
		}

		if (type === 'boolean') {
			if (op === FILTER_CLAUSE.eq) {
				const matched = options.find(
					(opt) =>
						opt.boolValue === initialValue ||
						(opt.boolValue === undefined &&
							((opt.value === FILTER_CLAUSE.yes &&
								initialValue === true) ||
								(opt.value === FILTER_CLAUSE.no &&
									initialValue === false))),
				);
				op = matched?.value ?? FILTER_CLAUSE.yes;
			} else if (op === FILTER_CLAUSE.notEq) {
				op = FILTER_CLAUSE.no;
			}
		}

		const initialOption =
			options.find((opt) => opt.value === op) || options[0];

		const nullOps = [FILTER_CLAUSE.null, FILTER_CLAUSE.notNull];
		const rangeOps = [FILTER_CLAUSE.between, FILTER_CLAUSE.inRange];
		let value = initialValue ?? '';

		if (
			!nullOps.includes(initialOption?.value) &&
			typeof initialValue === 'boolean'
		) {
			value = '';
		} else if (
			rangeOps.includes(initialOption?.value) &&
			!Array.isArray(initialValue) &&
			typeof initialValue !== 'string'
		) {
			value = [];
		} else if (type === 'enum' && !nullOps.includes(initialOption?.value)) {
			if (Array.isArray(initialValue)) {
				value = initialValue.map(String);
			} else if (
				initialValue !== '' &&
				initialValue != null &&
				typeof initialValue !== 'boolean'
			) {
				value = [String(initialValue)];
			} else {
				value = [];
			}
		}

		setSelectedOption(initialOption);
		setSelectedValue(value);
	}, [initialSelected, operatorFromUrl, options]);

	const handleOptionChange = useCallback(
		(_e, val) => {
			setSelectedOption(val);

			const nullOps = [FILTER_CLAUSE.null, FILTER_CLAUSE.notNull];
			const rangeOps = [FILTER_CLAUSE.between, FILTER_CLAUSE.inRange];

			if (rangeOps.includes(val?.value)) {
				setSelectedValue([]);
				return;
			}

			if (nullOps.includes(val?.value)) {
				return;
			}

			if (type === 'enum') {
				setSelectedValue([]);
				return;
			}

			if (
				type === 'date' &&
				DATE_TIME_OPS.includes(val?.value) &&
				DATE_TIME_OPS.includes(selectedOption?.value)
			) {
				setSelectedValue(
					(prev) => parseISODurationFilter(prev) || prev,
				);
				return;
			}

			setSelectedValue('');
		},
		[selectedOption, type],
	);

	const handleValueChange = useCallback((_e, value) => {
		setSelectedValue(value);
	}, []);

	const checkBeforeSubmit = () => {
		let isValid = true;

		// skip validation, because there is no value with these options
		if (
			selectedOption.value === 'isNull' ||
			selectedOption.value === 'isNotNull'
		) {
			return true;
		}

		if (
			type !== 'boolean' &&
			type !== 'enum' &&
			selectedOption.value !== FILTER_CLAUSE.inRange &&
			!selectedValue
		) {
			return false;
		}

		switch (type) {
			case 'boolean':
				break;
			case 'enum':
				if (
					!Array.isArray(selectedValue) ||
					selectedValue.length === 0
				) {
					isValid = false;
				}
				break;
			case 'string':
				if (
					selectedOption.value === FILTER_CLAUSE.inRange &&
					(!Array.isArray(selectedValue) ||
						!selectedValue[0] ||
						!selectedValue[1])
				) {
					isValid = false;
				}
				break;
			case 'date':
				if (selectedOption.value === FILTER_CLAUSE.between) {
					if (
						!Array.isArray(selectedValue) ||
						!selectedValue[0] ||
						!selectedValue[1]
					) {
						isValid = false;
					}
				} else if (!isISODurationFilterValue(selectedValue)) {
					isValid = false;
				}

				break;
		}

		return isValid;
	};

	const getResultValueForSubmit = () => {
		if (
			selectedOption.value === FILTER_CLAUSE.between ||
			selectedOption.value === FILTER_CLAUSE.inRange
		) {
			return `${selectedValue[0]}~${selectedValue[1]}`;
		}

		if (type === 'boolean') {
			if (selectedOption.boolValue !== undefined) {
				return selectedOption.boolValue;
			}

			if (selectedOption.value === 'yes') return true;
			if (selectedOption.value === 'no') return false;
		}

		if (type === 'date' && DATE_TIME_OPS.includes(selectedOption.value)) {
			return parseISODurationFilter(selectedValue) || selectedValue;
		}

		return selectedValue;
	};

	const handleSubmit = (isError) => {
		// console.log(getResultValueForSubmit());

		const isValid = checkBeforeSubmit();

		if (!isValid) {
			return;
		}

		if (isError) {
			console.error("Error in filter, can't submit");
			return;
		}

		if (onSubmit) {
			onSubmit({
				option: selectedOption,
				value: getResultValueForSubmit(),
			});
		}
	};

	return {
		selectedValue,
		selectedOption,
		error,
		setError,
		handleOptionChange,
		handleValueChange,
		handleSubmit,
	};
};
